// Clients → Clean up (Tim, 5 Oct 2026): suggested combines and Who is this. A suggestion is two or more customer records
// that look like one person (same phone, same email, same name, or an unnamed email that spells a client's name) where
// somebody spent money. Combine makes them one client in the app and, once the Reports-only Shopify app is installed, in
// Shopify too; Not the same hides the suggestion for good. Who is this lists everyone who bought and left no name, with a
// guess from the email where there is one. Every row opens the client's page with everything they bought.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Screen } from '../../ReportsApp'
import { reportsAction, useReport } from '../../api'
import { int, money } from '../../format'
import { AsyncButton, Badge, Tile } from '../../ui'
import { useOwnerSigner } from '../owner-edit'
import { Seg, dateY } from './clients-ui'

interface Acct { id: string; name: string; email: string; phone: string; city: string; spent: number; purchases: number; last: string | null; stub: boolean }
interface Suggestion { keep: string; accounts: Acct[]; why: string[]; sure: boolean; spent: number }
interface Who extends Acct { suggestion: string; sure: '' | 'likely' | 'maybe'; items: string[]; in_suggestion: boolean }
interface CleanupData { shopify_ready: boolean; suggestions: Suggestion[]; who: Who[]; pending: { id: string; name: string; status: string; note: string }[] }

function AcctCard({ a, keep, onKeep }: { a: Acct; keep: boolean; onKeep: () => void }) {
  return (
    <div className={'cu-acct' + (keep ? ' is-keep' : '')}>
      <div className="cu-acct__top">
        <Link to={'/clients/c/' + a.id} className="cu-acct__name">{a.name || <i>No name</i>}</Link>
        {keep ? <Badge tone="green">Keep this name</Badge> : a.name ? <button type="button" className="rp-linkish" onClick={onKeep}>Keep this one</button> : null}
      </div>
      <span>{a.email || <i className="rp-muted">no email</i>}</span>
      <span>{a.phone || <i className="rp-muted">no phone</i>}</span>
      <span className="cu-acct__money"><b>{money(a.spent, true)}</b> · {int(a.purchases)} {a.purchases === 1 ? 'purchase' : 'purchases'}{a.last ? ` · last ${dateY(a.last)}` : ''}</span>
      <Link to={'/clients/c/' + a.id} className="cu-acct__open">See what they bought →</Link>
    </div>
  )
}

export function ClientsCleanup() {
  const { data: d, error, loading, refresh } = useReport<CleanupData>('clients_cleanup')
  const signer = useOwnerSigner()
  const [tab, setTab] = useState<'combine' | 'who'>('combine')
  const [keepOf, setKeepOf] = useState<Record<string, string>>({})
  const [msg, setMsg] = useState('')
  const [ok, setOk] = useState('')
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [names, setNames] = useState<Record<string, string>>({})
  const sig = (s: Suggestion) => s.accounts.map((a) => a.id).sort().join('|')
  const sugg = useMemo(() => (d?.suggestions ?? []).filter((s) => !hidden.has(sig(s))), [d, hidden])
  const who = useMemo(() => (d?.who ?? []).filter((w) => !hidden.has(w.id)), [d, hidden])
  const act = (action: string, body: Record<string, unknown>, done: () => void, okText: string) => new Promise<void>((res, rej) => signer.signed(async () => {
    setMsg(''); setOk('')
    try { const r = await reportsAction<{ shopify?: { note?: string; done?: number; failed?: number } }>(action, { employee_id: signer.idRef.current, ...body }); done(); setOk(okText + (r.shopify?.note ? ` ${r.shopify.note}` : r.shopify?.done ? ' Combined in Shopify too.' : '')); res() } catch (e) { setMsg((e as Error).message); rej(e) }
  }))
  const combine = (s: Suggestion) => {
    const keep = keepOf[sig(s)] ?? s.keep
    return act('client_combine', { keep_id: keep, absorb_ids: s.accounts.map((a) => a.id).filter((i) => i !== keep), reason: s.why.join(', ') }, () => setHidden(new Set([...hidden, sig(s)])), `Combined into ${s.accounts.find((a) => a.id === keep)?.name || 'one client'}.`)
  }
  const notSame = (s: Suggestion) => act('client_not_same', { a: s.accounts[0].id, b: s.accounts[1].id }, () => setHidden(new Set([...hidden, sig(s)])), 'Hidden: they stay separate.')
  const saveName = (w: Who, raw: string) => {
    const parts = raw.trim().replace(/\s+/g, ' ').split(' ')
    return act('client_name', { customer_id: w.id, first_name: parts[0] ?? '', last_name: parts.slice(1).join(' '), from_suggestion: raw.trim() === w.suggestion }, () => setHidden(new Set([...hidden, w.id])), `${raw.trim()} is now a client.`)
  }
  const usable = (w: Who) => !!w.suggestion && !w.suggestion.includes('unknown') && !w.suggestion.startsWith('?') && !/^[A-Z]\. /.test(w.suggestion)
  const pending = (d?.pending ?? []).filter((p) => p.status === 'pending').length
  const failed = (d?.pending ?? []).filter((p) => p.status === 'failed')
  return (
    <Screen title="Clean up" sub="Combine records that are one person, and name the people who bought without leaving a name"
      right={<Seg label="Section" value={tab} onChange={setTab} options={[['combine', `Suggested combines ${d ? sugg.length : ''}`], ['who', `Who is this ${d ? who.length : ''}`]]} />}>
      {signer.askEl}
      {(error || msg) && <div className="rp-error">{error || msg}</div>}
      {ok && <div className="rp-okbar">{ok}</div>}
      <div className="rp-grid rp-grid--tiles">
        <Tile label="Suggested combines" value={d ? int(sugg.length) : '…'} sub={d ? `${money(sugg.reduce((a, s) => a + s.spent, 0), true)} of spend sits on split records` : ''} />
        <Tile label="Bought, no name" tone={who.length ? 'amber' : 'plain'} value={d ? int(who.length) : '…'} sub={d ? `${money(who.reduce((a, w) => a + w.spent, 0), true)} spent. ${int(who.filter(usable).length)} have a name guess` : ''} />
        <Tile label="Waiting for Shopify" tone={failed.length ? 'red' : 'plain'} value={d ? int(pending + failed.length) : '…'}
          sub={d ? (d.shopify_ready ? (failed.length ? `${failed.length} Shopify would not merge: open the list below` : 'Combined in the app; Shopify merges run on Combine') : 'Combined in the app. Shopify follows once the Reports Shopify app is installed') : ''}
          badge={d?.shopify_ready && pending ? <AsyncButton small run={() => act('clients_shopify_merge', {}, refresh, 'Shopify merges run.')}>Run now</AsyncButton> : undefined} />
      </div>
      {loading && !d && <div className="rp-empty">…</div>}
      {d && tab === 'combine' && (
        <div className="cu-list">
          {!sugg.length && <div className="rp-card"><div className="rp-empty">Nothing to combine. New suggestions appear here as customers come in.</div></div>}
          {sugg.map((s) => {
            const keep = keepOf[sig(s)] ?? s.keep
            return (
              <div key={sig(s)} className="rp-card cu-sugg">
                <div className="cu-sugg__head">
                  <div><b>{s.accounts.find((a) => a.id === keep)?.name || 'Unnamed'}</b> <span className="rp-muted">· {money(s.spent, true)} across {s.accounts.length} records</span></div>
                  <div className="cu-sugg__why">{s.sure ? <Badge tone="green">Same contact details</Badge> : <Badge tone="amber">Check</Badge>}{s.why.map((w) => <span key={w}>{w}</span>)}</div>
                </div>
                <div className="cu-sugg__accts">{s.accounts.map((a) => <AcctCard key={a.id} a={a} keep={a.id === keep} onKeep={() => setKeepOf({ ...keepOf, [sig(s)]: a.id })} />)}</div>
                <div className="cu-sugg__foot">
                  <AsyncButton primary run={() => combine(s)}>Combine</AsyncButton>
                  <AsyncButton run={() => notSame(s)}>Not the same person</AsyncButton>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {d && tab === 'who' && (
        <div className="rp-card">
          <div className="cu-who cu-who--head"><span>Customer</span><span className="r">Spent</span><span>What they bought</span><span>Name</span></div>
          {!who.length && <div className="rp-empty">Everyone who has bought has a name.</div>}
          {who.map((w) => {
            const v = names[w.id] ?? (usable(w) ? w.suggestion : '')
            return (
              <div key={w.id} className="cu-who">
                <span className="cl-who"><Link to={'/clients/c/' + w.id}><b>{w.email || w.phone}</b></Link><small>{int(w.purchases)} {w.purchases === 1 ? 'purchase' : 'purchases'}{w.last ? `, last ${dateY(w.last)}` : ''}{w.in_suggestion ? ' · also in a suggested combine' : ''}</small></span>
                <span className="r"><b>{money(w.spent, true)}</b></span>
                <span className="cu-who__items"><Link to={'/clients/c/' + w.id}>{w.items.length ? w.items.join(' · ') : 'See purchases'}</Link></span>
                <span className="cu-who__name">
                  <input className="rp-input" value={v} placeholder={w.suggestion && !usable(w) ? `Hint: ${w.suggestion.replace(' (last name unknown)', '…')}` : 'First and last name'} onChange={(e) => setNames({ ...names, [w.id]: e.target.value })} aria-label={`Name for ${w.email || w.phone}`} />
                  {usable(w) && v === w.suggestion && <Badge tone={w.sure === 'likely' ? 'green' : 'amber'}>{w.sure === 'likely' ? 'Guess from email' : 'Rough guess'}</Badge>}
                  <AsyncButton small primary disabled={!v.trim()} run={() => saveName(w, v)}>Save</AsyncButton>
                </span>
              </div>
            )
          })}
        </div>
      )}
      {d && failed.length > 0 && (
        <div className="rp-card"><div className="cl-tablehead"><b>Shopify would not merge these</b></div>
          {failed.map((f) => <div key={f.id} className="cu-who"><span>{f.name}</span><span /><span className="rp-tone-red">{f.note}</span><span /></div>)}</div>
      )}
    </Screen>
  )
}
